import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
} from '../api/auth.js'
import { clearAccessToken, getAccessToken, setAccessToken } from '../api/client.js'

const AuthContext = createContext(null)

const ROLE_PERMISSIONS = {
  view_applications: new Set(['Viewer', 'QA', 'Game Designer', 'Producer', 'Admin']),
  manage_users: new Set(['Admin']),
  create_application: new Set(['Producer', 'Admin']),
  validator: new Set(['Producer', 'Admin']),
  force_updater: new Set(['Producer', 'Admin']),
  create_config: new Set(['Game Designer', 'Producer', 'Admin']),
  edit_config: new Set(['Game Designer', 'Producer', 'Admin']),
  manage_elements: new Set(['Game Designer', 'Producer', 'Admin']),
  apply_config_versions: new Set(['Game Designer', 'Producer', 'Admin']),
  archive_config: new Set(['Producer', 'Admin']),
  config_to_fixing: new Set(['Game Designer', 'Producer', 'Admin']),
  config_to_testing: new Set(['Game Designer', 'Producer', 'Admin']),
  config_to_failed_tested: new Set(['QA', 'Admin']),
  config_to_verified: new Set(['Game Designer', 'Producer', 'Admin']),
  config_to_released: new Set(['Producer', 'Admin']),
  qa_features: new Set(['QA', 'Game Designer', 'Producer', 'Admin']),
  create_ab_test: new Set(['Game Designer', 'Producer', 'Admin']),
  start_ab_test: new Set(['Producer', 'Admin']),
  stop_distribution_ab: new Set(['Producer', 'Admin']),
  finish_ab_test: new Set(['Producer', 'Admin']),
  create_scheduler: new Set(['Game Designer', 'Producer', 'Admin']),
  read_scheduler: new Set(['QA', 'Game Designer', 'Producer', 'Admin', 'Support']),
  edit_scheduler: new Set(['Game Designer', 'Producer', 'Admin']),
  delete_scheduler: new Set(['Game Designer', 'Producer', 'Admin']),
  manage_events: new Set(['Game Designer', 'Producer', 'Admin']),
  deletion_request: new Set(['Admin', 'Support']),
  view_activity_logs: new Set(['Admin']),
  manage_work_groups: new Set(['Admin']),
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const bootstrap = useCallback(async () => {
    try {
      if (!getAccessToken()) {
        const refreshed = await apiRefreshToken()
        if (refreshed?.access_token) {
          setAccessToken(refreshed.access_token)
        }
      }
      const me = await fetchMe()
      setUser(me)
    } catch {
      clearAccessToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  const login = useCallback(async (googleCredential) => {
    const data = await apiLogin(googleCredential)
    setAccessToken(data.access_token)
    const me = await fetchMe()
    setUser(me)
    return me
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch { /* ignore */ }
    clearAccessToken()
    setUser(null)
  }, [])

  const hasPermission = useCallback(
    (permissionKey) => {
      if (!user) return false
      const allowed = ROLE_PERMISSIONS[permissionKey]
      return allowed ? allowed.has(user.role) : false
    },
    [user],
  )

  const value = useMemo(
    () => ({ user, loading, login, logout, hasPermission }),
    [user, loading, login, logout, hasPermission],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
